import { FC, useEffect } from 'react';
import PositionTable from './table/table.component';
import ExposureGraphics from './exposureGraphics/exposureGraphics.component';
import CreditRatingsTable from './creditRatings/creditRatings.component';
import AggregateFixedIncomeTable from './aggTable/aggTable.component';
import useTitleBarSelect from '../../../../hooks/useTitleBarSelect';
import { useDispatch } from 'react-redux';
import { clearPdfExports } from '../../../../redux/fileExport/actions';
import { BubbleChart } from '@mui/icons-material';
import { Select } from '../../../../types/redux/pages/PagesStore';
import {
  addAdditionalTitleBarComponents,
  removeTitleBarComponent,
} from '../../../../redux/pages/actions';
import {
  APPEND_NEW_SELECT_TO_TITLEBAR,
  REMOVE_SELECT_FROM_TITLEBAR,
} from '../../../../types/redux/pages/pageTypes';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useAssetClassReportPageModel } from '../../../../api/rs-hg/asset-class/hooks/queries';
import { BondDataServer, isBondDataServer } from './table/table.data';

export interface FixedIncomeComponent {
  fixedIncomeData: BondDataServer;
  isFetching: boolean;
}

export type FixedIncomePages =
  | 'positionTable'
  | 'exposureGraphics'
  | 'creditRatingsTable'
  | 'bubbleChart';

export enum FixedIncomeOption {
  PC_OF_NAV = 'Percentage of NAV',
  BASE_CURRENCY = 'Base Currency',
}

const handleComponent = (selectedPage: string | null) => {
  switch (selectedPage) {
    case 'positionTable':
      return PositionTable;
    case 'exposureGraphics':
      return ExposureGraphics;
    case 'creditRatingsTable':
      return CreditRatingsTable;
    case 'bubbleChart':
      return BubbleChart;
    default:
      return PositionTable;
  }
};

const FixedIncome: FC<FundInfoComponentProps> = ({
  fundId,
  fundName,
  positionDate,
}) => {
  const dispatch = useDispatch();

  // Add the select components to the title bar
  useEffect(() => {
    const selectsToAdd: Select[] = [
      {
        titleBarTitle: 'Select Output: ',
        titleBarKey: 'dataOutput',
        values: [
          {
            label: 'Bonds and Derivatives',
            value: 'bondsAndDerivatives',
          },
          {
            label: 'Bonds',
            value: 'bonds',
          },
        ],
        displayOnOverviewPage: false,
        aValueIsAlwaysSelected: true,
        currentValue: 'bondsAndDerivatives',
      },
      {
        titleBarTitle: 'Visualisation: ',
        titleBarKey: 'selectVisualisation',
        values: [
          {
            label: 'Position Table',
            value: 'positionTable',
          },
          {
            label: 'Exposure Graphics',
            value: 'exposureGraphics',
          },
          {
            label: 'Credit Ratings Table',
            value: 'creditRatingsTable',
          },
          {
            label: 'Bubble Chart',
            value: 'bubbleChart',
          },
        ],
        displayOnOverviewPage: false,
        aValueIsAlwaysSelected: true,
        currentValue: 'positionTable',
      },
    ];

    dispatch(
      addAdditionalTitleBarComponents(
        selectsToAdd,
        APPEND_NEW_SELECT_TO_TITLEBAR,
      ),
    );

    return () => {
      dispatch(
        removeTitleBarComponent('dataOutput', REMOVE_SELECT_FROM_TITLEBAR),
      );
      dispatch(
        removeTitleBarComponent(
          'selectVisualisation',
          REMOVE_SELECT_FROM_TITLEBAR,
        ),
      );
    };
  }, []);

  const selectedOutput = useTitleBarSelect('dataOutput');
  const selectedPage = useTitleBarSelect('selectVisualisation');

  const SelectedComponent = handleComponent(selectedPage);

  useEffect(() => {
    dispatch(clearPdfExports());
  }, [selectedPage, selectedOutput]);

  const suffix =
    selectedOutput === 'bonds'
      ? 'bond_only_specific_data'
      : 'bond_specific_data';

  const fixedIncomeEnabled = selectedPage !== null && selectedOutput !== null;
  const { data, isLoading, isFetching, isError, error } = useAssetClassReportPageModel<BondDataServer>(
    {
      fundId,
      reportKey: suffix,
      positionDate,
      isReportData: isBondDataServer,
    },
    { enabled: fixedIncomeEnabled },
  );

  return (
    <QueryBoundary<BondDataServer>
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      error={error}
      data={data?.data}
      noDataMessage={'No data found for ' + fundName}
      errorMessage={'No data found for ' + fundName}
    >
      {(report) => (
        <>
          <AggregateFixedIncomeTable
            currentPage={(selectedPage as FixedIncomePages) || 'positionTable'}
            fixedIncomeData={report}
            isFetching={isFetching}
          />
          <SelectedComponent
            fixedIncomeData={report}
            isFetching={isFetching}
          />
        </>
      )}
    </QueryBoundary>
  );
};

export default FixedIncome;
